import type { QuoteDetailsSummary } from '@/lib/api/quote-details'
import Decimal from 'decimal.js'

export const SUPPLIES_RATE_PRECISION = 8

export type LockedSuppliesRates = {
  priceRate: number
  costRate: number
}

export type SuppliesAmounts = {
  price: number
  cost: number
}

type StoredSuppliesDetail = {
  priceperunit?: number | null
  msdyn_costpriceperunit?: number | null
  msdyn_costamount?: number | null
  quantity?: number | null
}

type SuppliesSyncPlanInput = {
  currentRates: {
    priceRate: number | null | undefined
    costRate: number | null | undefined
  }
  desiredRates: LockedSuppliesRates | null
  currentDetail: {
    price: number | null | undefined
    cost: number | null | undefined
    costAmount: number | null | undefined
    tax: number | null | undefined
  }
  desiredDetail: {
    price: number
    cost: number
    tax: number
  }
}

export function getSuppliesCalculationBase(summary: QuoteDetailsSummary) {
  return new Decimal(summary.subtotal).sub(summary.discounts.products).sub(summary.discounts.labor).toNumber()
}

export function getSuppliesRateForAmount(amount: number, calculationBase: number) {
  const targetAmount = new Decimal(amount).toDecimalPlaces(2)

  if (targetAmount.isNegative()) return null
  if (targetAmount.isZero()) return 0

  const base = new Decimal(calculationBase)
  if (base.lte(0)) return null

  const exactRate = targetAmount.div(base.plus(targetAmount)).mul(100)
  const roundedRate = exactRate.toDecimalPlaces(SUPPLIES_RATE_PRECISION)
  const rateStep = new Decimal(10).pow(-SUPPLIES_RATE_PRECISION)
  const candidates = [roundedRate, roundedRate.sub(rateStep), roundedRate.add(rateStep)]

  const matchingRate = candidates.find((rate) => {
    if (rate.isNegative() || rate.gte(100)) return false

    const rateDecimal = rate.div(100)
    const calculatedAmount = rateDecimal.mul(base).div(new Decimal(1).sub(rateDecimal)).toDecimalPlaces(2)
    return calculatedAmount.equals(targetAmount)
  })

  return matchingRate?.toNumber() ?? null
}

export function getStoredSuppliesAmounts(details: StoredSuppliesDetail[]): SuppliesAmounts | null {
  if (!details.length) return null

  const amounts = details.reduce(
    (total, detail) => {
      const quantity = new Decimal(detail.quantity ?? 0)
      const price = new Decimal(detail.priceperunit ?? 0).mul(quantity)
      const cost =
        detail.msdyn_costpriceperunit === null || detail.msdyn_costpriceperunit === undefined
          ? new Decimal(detail.msdyn_costamount ?? 0)
          : new Decimal(detail.msdyn_costpriceperunit).mul(quantity)

      return {
        price: total.price.plus(price),
        cost: total.cost.plus(cost),
      }
    },
    { price: new Decimal(0), cost: new Decimal(0) }
  )

  return {
    price: amounts.price.toDecimalPlaces(2).toNumber(),
    cost: amounts.cost.toDecimalPlaces(2).toNumber(),
  }
}

export function getLockedSuppliesRates(
  currentSummary: QuoteDetailsSummary,
  previousSupplies: SuppliesAmounts
): LockedSuppliesRates | null {
  const calculationBase = getSuppliesCalculationBase(currentSummary)
  const priceRate = getSuppliesRateForAmount(previousSupplies.price, calculationBase)
  const costRate = getSuppliesRateForAmount(previousSupplies.cost, calculationBase)

  if (priceRate === null || costRate === null) return null

  return { priceRate, costRate }
}

export function suppliesRatesEqual(left: number | null | undefined, right: number) {
  return new Decimal(left ?? 0)
    .toDecimalPlaces(SUPPLIES_RATE_PRECISION)
    .equals(new Decimal(right).toDecimalPlaces(SUPPLIES_RATE_PRECISION))
}

export function getSuppliesSyncPlan({
  currentRates,
  desiredRates,
  currentDetail,
  desiredDetail,
}: SuppliesSyncPlanInput) {
  const ratesNeedUpdate =
    desiredRates !== null &&
    (!suppliesRatesEqual(currentRates.priceRate, desiredRates.priceRate) ||
      !suppliesRatesEqual(currentRates.costRate, desiredRates.costRate))
  const detailNeedsUpdate =
    !new Decimal(currentDetail.price ?? 0).toDecimalPlaces(2).equals(new Decimal(desiredDetail.price).toDecimalPlaces(2)) ||
    !new Decimal(currentDetail.cost ?? 0).toDecimalPlaces(2).equals(new Decimal(desiredDetail.cost).toDecimalPlaces(2)) ||
    !new Decimal(currentDetail.costAmount ?? 0)
      .toDecimalPlaces(2)
      .equals(new Decimal(desiredDetail.cost).toDecimalPlaces(2)) ||
    !new Decimal(currentDetail.tax ?? 0).toDecimalPlaces(2).equals(new Decimal(desiredDetail.tax).toDecimalPlaces(2))

  return {
    ratesNeedUpdate,
    detailNeedsUpdate,
    isDirty: ratesNeedUpdate || detailNeedsUpdate,
  }
}
